import type { Tab } from './index';
import { esc, timeAgo } from '../utils';
import { workerFetch } from '../worker';

interface DeFiChain    { name: string; tvl: number; symbol: string; }
interface DeFiProtocol { name: string; tvl: number; category: string; chain: string; change1d: number|null; change7d: number|null; logo: string; symbol: string; }
interface DeFiCategory { name: string; tvl: number; }
interface DeFiResp     { totalTVL: number; chains: DeFiChain[]; protocols: DeFiProtocol[]; categories: DeFiCategory[]; cachedAt: string; }

function fmtTVL(n: number): string {
  if (n >= 1e12) return `$${(n/1e12).toFixed(2)}T`;
  if (n >= 1e9)  return `$${(n/1e9).toFixed(2)}B`;
  if (n >= 1e6)  return `$${(n/1e6).toFixed(1)}M`;
  return `$${n.toFixed(0)}`;
}

function changeBadge(pct: number|null): string {
  if (pct == null) return '<span style="color:var(--text3);">—</span>';
  const col = pct >= 0 ? 'var(--green)' : 'var(--red)';
  return `<span style="color:${col};font-family:var(--mono);font-size:11px;">${pct>=0?'+':''}${pct.toFixed(1)}%</span>`;
}

export class DeFiMarketsTab implements Tab {
  readonly id = 'defi-markets';
  private loaded    = false;
  private activecat = 'All';

  private el(id: string): HTMLElement { return document.getElementById(id) as HTMLElement; }

  init(): void {
    // defi-refresh-btn is in HTML; defi-cat-filter is dynamically created → use delegation on defi-body
    document.getElementById('defi-refresh-btn')?.addEventListener('click', () => { this.loaded = false; this.load(); });
    document.getElementById('defi-body')?.addEventListener('click', (e: MouseEvent) => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-defi-cat]');
      if (!btn) return;
      this.activecat = btn.dataset.defiCat!;
      const protos = document.getElementById('defi-protos');
      if (protos?.dataset.raw) this.renderProtocols(JSON.parse(protos.dataset.raw), this.activecat);
      document.querySelectorAll<HTMLElement>('[data-defi-cat]').forEach(b =>
        b.style.background = b.dataset.defiCat === this.activecat ? 'rgba(0,212,255,0.15)' : 'transparent'
      );
    });
  }

  render(): void {
    if (!this.loaded) this.load();
  }

  private async load(): Promise<void> {
    this.el('defi-body').innerHTML = `<div style="padding:20px;color:var(--text3);font-family:var(--mono);font-size:12px;">Loading DeFi data from DeFiLlama…</div>`;
    try {
      const data = await workerFetch<DeFiResp>('/api/defi-markets');
      this.loaded = true;
      this.el('defi-updated').textContent = `Cached ${timeAgo(data.cachedAt)}`;
      this.render_body(data);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      this.el('defi-body').innerHTML = `<div style="padding:20px;color:var(--red);font-family:var(--mono);font-size:12px;">Error: ${esc(msg)}</div>`;
    }
  }

  private render_body(data: DeFiResp): void {
    // Total TVL hero
    const hero = `
      <div style="background:var(--panel);border:1px solid var(--border);border-radius:4px;padding:18px 22px;margin-bottom:16px;display:flex;gap:32px;align-items:center;flex-wrap:wrap;">
        <div>
          <div style="font-size:10px;letter-spacing:0.12em;text-transform:uppercase;color:var(--text3);margin-bottom:4px;">Total DeFi TVL</div>
          <div style="font-family:var(--mono);font-size:28px;font-weight:700;color:var(--accent);">${esc(fmtTVL(data.totalTVL))}</div>
          <div style="font-size:10px;color:var(--text3);margin-top:3px;">Excluding CEX · Source: DeFiLlama</div>
        </div>
        <div style="display:flex;gap:16px;flex-wrap:wrap;">
          ${data.categories.slice(0,5).map(c => `
            <div style="text-align:center;">
              <div style="font-size:10px;color:var(--text3);margin-bottom:2px;">${esc(c.name)}</div>
              <div style="font-family:var(--mono);font-size:13px;font-weight:700;color:var(--text2);">${esc(fmtTVL(c.tvl))}</div>
            </div>`).join('')}
        </div>
      </div>`;

    // Top chains
    const chains = `
      <div style="margin-bottom:16px;">
        <div style="font-size:10px;letter-spacing:0.12em;text-transform:uppercase;color:var(--text3);margin-bottom:8px;">Top Chains by TVL</div>
        <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(140px,1fr));gap:8px;">
          ${data.chains.slice(0,10).map((c, i) => `
            <div style="background:var(--panel);border:1px solid var(--border);border-radius:3px;padding:10px 12px;">
              <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;">
                <span style="font-size:10px;color:var(--text3);font-family:var(--mono);">#${i+1}</span>
                <span style="font-family:var(--mono);font-size:12px;font-weight:700;color:var(--text);">${esc(c.name)}</span>
              </div>
              <div style="font-family:var(--mono);font-size:13px;font-weight:700;color:var(--accent);">${esc(fmtTVL(c.tvl))}</div>
            </div>`).join('')}
        </div>
      </div>`;

    // Category filter
    const cats = ['All', ...data.categories.slice(0,8).map(c => c.name)];
    const catBar = `<div id="defi-cat-filter" style="display:flex;gap:5px;flex-wrap:wrap;margin-bottom:10px;">
      ${cats.map(c => `<button data-defi-cat="${esc(c)}" style="background:${c===this.activecat?'rgba(0,212,255,0.15)':'transparent'};border:1px solid var(--border);color:var(--text2);font-family:var(--mono);font-size:10px;padding:3px 8px;cursor:pointer;border-radius:2px;">${esc(c)}</button>`).join('')}
    </div>`;

    // Protocols table
    const protoContainer = `
      <div>
        <div style="font-size:10px;letter-spacing:0.12em;text-transform:uppercase;color:var(--text3);margin-bottom:6px;">Top Protocols</div>
        ${catBar}
        <div id="defi-protos"></div>
      </div>`;

    this.el('defi-body').innerHTML = hero + chains + protoContainer;

    // Store raw for filter re-renders
    const protosEl = this.el('defi-protos');
    protosEl.dataset.raw = JSON.stringify(data.protocols);
    this.renderProtocols(data.protocols, this.activecat);
  }

  private renderProtocols(protocols: DeFiProtocol[], cat: string): void {
    const filtered = cat === 'All' ? protocols : protocols.filter(p => p.category === cat);
    const el = this.el('defi-protos');
    el.innerHTML = `
      <table style="width:100%;border-collapse:collapse;font-size:12px;">
        <thead>
          <tr style="border-bottom:2px solid var(--border);">
            <th style="padding:6px 10px;text-align:left;font-size:10px;letter-spacing:0.1em;color:var(--text3);text-transform:uppercase;">Protocol</th>
            <th style="padding:6px 10px;text-align:left;font-size:10px;letter-spacing:0.1em;color:var(--text3);text-transform:uppercase;">Category</th>
            <th style="padding:6px 10px;text-align:left;font-size:10px;letter-spacing:0.1em;color:var(--text3);text-transform:uppercase;">Chain</th>
            <th style="padding:6px 10px;text-align:right;font-size:10px;letter-spacing:0.1em;color:var(--text3);text-transform:uppercase;">TVL</th>
            <th style="padding:6px 10px;text-align:right;font-size:10px;letter-spacing:0.1em;color:var(--text3);text-transform:uppercase;">1d</th>
            <th style="padding:6px 10px;text-align:right;font-size:10px;letter-spacing:0.1em;color:var(--text3);text-transform:uppercase;">7d</th>
          </tr>
        </thead>
        <tbody>
          ${filtered.slice(0,25).map((p, i) => `
            <tr style="border-bottom:1px solid var(--border);">
              <td style="padding:9px 10px;">
                <div style="display:flex;align-items:center;gap:7px;">
                  ${p.logo ? `<img src="${esc(p.logo)}" style="width:18px;height:18px;border-radius:50%;object-fit:cover;" onerror="this.style.display='none'">` : ''}
                  <div>
                    <div style="font-weight:700;color:var(--text);">${esc(p.name)}</div>
                    ${p.symbol ? `<div style="font-size:10px;color:var(--text3);font-family:var(--mono);">${esc(p.symbol)}</div>` : ''}
                  </div>
                </div>
              </td>
              <td style="padding:9px 10px;"><span style="background:var(--bg3);border:1px solid var(--border);border-radius:2px;font-size:10px;padding:1px 5px;color:var(--text2);">${esc(p.category)}</span></td>
              <td style="padding:9px 10px;font-family:var(--mono);font-size:11px;color:var(--text3);">${esc(p.chain)}</td>
              <td style="padding:9px 10px;text-align:right;font-family:var(--mono);font-weight:700;color:var(--accent);">${esc(fmtTVL(p.tvl))}</td>
              <td style="padding:9px 10px;text-align:right;">${changeBadge(p.change1d)}</td>
              <td style="padding:9px 10px;text-align:right;">${changeBadge(p.change7d)}</td>
            </tr>`).join('')}
        </tbody>
      </table>`;
  }
}
